/**
 * Records a call in the browser: the call's own audio (a shared tab or screen with "Share
 * audio" ticked) mixed with the microphone, so both sides are on the recording. No upload:
 * the recording stays in the page until it is transcribed.
 */
import { useEffect, useRef, useState } from "react";

export type RecorderState = "idle" | "recording" | "stopped" | "error";

/** Browsers without screen capture (most phones) can't record the other side of a call. */
export const canRecordCalls = (): boolean =>
  typeof navigator !== "undefined" &&
  !!navigator.mediaDevices?.getDisplayMedia &&
  typeof MediaRecorder !== "undefined";

const stopTracks = (streams: MediaStream[]) =>
  streams.forEach((stream) => stream.getTracks().forEach((t) => t.stop()));

export const useCallRecorder = () => {
  const [state, setState] = useState<RecorderState>("idle");
  const [error, setError] = useState("");
  const [recording, setRecording] = useState<Blob | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const streams = useRef<MediaStream[]>([]);
  const audioContext = useRef<AudioContext | null>(null);

  const cleanUp = () => {
    stopTracks(streams.current);
    streams.current = [];
    void audioContext.current?.close();
    audioContext.current = null;
  };

  useEffect(() => cleanUp, []);

  const start = async () => {
    setError("");
    setRecording(null);
    try {
      const call = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });
      const mic = await navigator.mediaDevices
        .getUserMedia({ audio: true })
        .catch(() => null);
      streams.current = mic ? [call, mic] : [call];
      if (call.getAudioTracks().length === 0) {
        throw new Error(
          'No call audio: share the call\'s tab or screen and tick "Share audio".',
        );
      }

      const context = new AudioContext();
      audioContext.current = context;
      const mixed = context.createMediaStreamDestination();
      for (const stream of streams.current) {
        context.createMediaStreamSource(stream).connect(mixed);
      }

      const chunks: Blob[] = [];
      const mediaRecorder = new MediaRecorder(mixed.stream);
      mediaRecorder.ondataavailable = (event) => chunks.push(event.data);
      mediaRecorder.onstop = () => {
        setRecording(new Blob(chunks, { type: mediaRecorder.mimeType }));
        setState("stopped");
        cleanUp();
      };
      // Ending the share from the browser's own bar stops the recording too.
      call.getVideoTracks()[0]?.addEventListener("ended", () => {
        if (mediaRecorder.state === "recording") mediaRecorder.stop();
      });
      mediaRecorder.start();
      recorder.current = mediaRecorder;
      setStartedAt(Date.now());
      setState("recording");
    } catch (caught) {
      cleanUp();
      setError(
        caught instanceof Error ? caught.message : "Recording could not start.",
      );
      setState("error");
    }
  };

  const stop = () => {
    if (recorder.current?.state === "recording") recorder.current.stop();
  };

  const reset = () => {
    setRecording(null);
    setStartedAt(null);
    setState("idle");
  };

  return { state, error, recording, startedAt, start, stop, reset };
};
